export interface TokenInfo {
  accessToken: string;
  expiresAt: number;
  scopes: string[];
}

export type TokenRefreshCallback = () => Promise<string | null>;

/**
 * Singleton service for managing OAuth tokens and authentication state.
 * 
 * This service acts as a bridge between MSAL and the rest of the application,
 * providing centralized access to authentication tokens. It handles:
 * - Token caching in memory for fast access
 * - Token persistence in localStorage for multi-tab support
 * - Automatic expiration checking
 * - Token refresh when expired or about to expire
 * - Centralized token clearing on logout
 * 
 * @example
 * ```typescript
 * // Set token after MSAL authentication
 * oAuthService.setAccessToken(token, expiresOn);
 * 
 * // Get token in API clients
 * const token = oAuthService.getAccessToken();
 * 
 * // Register token refresh callback
 * oAuthService.setTokenRefreshCallback(async () => {
 *   const newToken = await acquireTokenSilently();
 *   return newToken;
 * });
 * 
 * // Clear token on logout
 * oAuthService.clearToken();
 * ```
 */
class OAuthService {
  private readonly STORAGE_KEY = 'oauth_tokens';
  private cachedToken: string | null = null;
  private tokenExpirationTime: number | null = null;
  private tokenRefreshCallback: TokenRefreshCallback | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;
  
  // Token is considered expiring if it has less than 5 minutes left
  private static readonly TOKEN_EXPIRY_THRESHOLD_MS = 5 * 60 * 1000;

  /**
   * Set the access token from MSAL authentication.
  * Stores token in both memory cache and localStorage.
   * 
   * @param token - The access token from MSAL
   * @param expiresOn - Optional expiration date of the token
   * 
   * @remarks
   * This should be called by useAzureAuth whenever a new token is acquired.
  * The token is stored in localStorage to enable multi-tab support.
   */
  setAccessToken(token: string, expiresOn?: Date): void {
    this.cachedToken = token;
    
    if (expiresOn) {
      this.tokenExpirationTime = expiresOn.getTime();
      
      const tokenInfo: TokenInfo = {
        accessToken: token,
        expiresAt: expiresOn.getTime(),
        scopes: []
      };
      this.storeTokens(tokenInfo);
    }
    
    // Reset refresh state when new token is set
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  /**
   * Register a callback to refresh the token when it expires.
   * This callback should acquire a new token from MSAL.
   * 
   * @param callback - Function that returns a promise with the new token
   */
  setTokenRefreshCallback(callback: TokenRefreshCallback): void {
    this.tokenRefreshCallback = callback;
  }

  /**
   * Get the current access token.
   * 
   * Returns cached token if available and not expired,
  * otherwise attempts to retrieve from localStorage.
   * If token is expired or about to expire, attempts to refresh it.
   * 
   * @returns The current access token
   * @throws Error if no valid token is available
   * 
   * @remarks
   * This is the primary method used by API clients to get tokens.
   * It automatically checks expiration and attempts refresh if needed.
   */
  async getAccessToken(): Promise<string> {
    // Load from cache or storage
    if (!this.cachedToken || !this.tokenExpirationTime) {
      const tokenInfo = this.getStoredTokens();
      if (tokenInfo) {
        this.cachedToken = tokenInfo.accessToken;
        this.tokenExpirationTime = tokenInfo.expiresAt;
      }
    }

    if (!this.cachedToken || !this.tokenExpirationTime) {
      throw new Error('No access token available. Please authenticate first.');
    }

    // Refresh if expired or expiring soon
    if (this.isTokenExpired() || this.isTokenExpiring()) {
      console.log('[oAuthService] Token expired or expiring soon, attempting refresh...');
      const refreshedToken = await this.refreshToken();
      if (refreshedToken) {
        return refreshedToken;
      }
      // If refresh failed but token not yet expired, use current token
      if (!this.isTokenExpired()) {
        console.warn('[oAuthService] Token refresh failed, using current token');
        return this.cachedToken;
      }
      throw new Error('Token expired and refresh failed. Please authenticate again.');
    }

    return this.cachedToken;
  }

  /**
   * Get the current access token synchronously (for backward compatibility).
   * Does not attempt token refresh.
   * 
   * @returns The current access token
   * @throws Error if no valid token is available
   */
  getAccessTokenSync(): string {
    // Check in-memory cache first (fastest)
    if (this.cachedToken && this.tokenExpirationTime && this.tokenExpirationTime > Date.now()) {
      return this.cachedToken;
    }

    // Fall back to storage
    const tokenInfo = this.getStoredTokens();
    if (tokenInfo) {
      this.cachedToken = tokenInfo.accessToken;
      this.tokenExpirationTime = tokenInfo.expiresAt;
      return tokenInfo.accessToken;
    }

    throw new Error('No access token available. Please authenticate first.');
  }

  /**
   * Refresh the access token using the registered callback.
   * Ensures only one refresh operation happens at a time.
   * 
   * @returns The new access token or null if refresh failed
   */
  async refreshToken(): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      console.log('[oAuthService] Token refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    if (!this.tokenRefreshCallback) {
      console.warn('[oAuthService] No token refresh callback registered');
      return null;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        console.log('[oAuthService] Refreshing token...');
        const newToken = await this.tokenRefreshCallback!();
        if (newToken) {
          console.log('[oAuthService] Token refreshed successfully');
          return newToken;
        }
        console.warn('[oAuthService] Token refresh returned null');
        return null;
      } catch (error) {
        console.error('[oAuthService] Token refresh failed:', error);
        return null;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Clear all authentication data.
  * Removes token from both memory cache and localStorage.
   * 
   * @remarks
   * Should be called during logout to ensure complete cleanup.
   */
  clearToken(): void {
    this.cachedToken = null;
    this.tokenExpirationTime = null;
    this.clearStoredTokens();
  }

  /**
   * Get stored tokens from localStorage (shared across tabs)
   */
  private getStoredTokens(): TokenInfo | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }
      
      const tokenInfo: TokenInfo = JSON.parse(stored);
      
      // Check if token is expired
      if (tokenInfo.expiresAt <= Date.now()) {
        this.clearStoredTokens();
        return null;
      }
      return tokenInfo;
    } catch (error) {
      console.error('Error retrieving stored tokens:', error);
      return null;
    }
  }

  /**
   * Store tokens in localStorage (shared across tabs)
   */
  private storeTokens(tokenInfo: TokenInfo): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tokenInfo));
    } catch (error) {
      console.error('Error storing tokens:', error);
      throw error;
    }
  }

  /**
   * Clear stored tokens
   */
  private clearStoredTokens(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Get current access token if available and not expired
   * Returns null instead of throwing an error (for backward compatibility)
   */
  getCurrentAccessToken(): string | null {
    try {
      return this.getAccessTokenSync();
    } catch {
      return null;
    }
  }

  /**
   * Check if we have valid tokens
   */
  isAuthenticated(): boolean {
    try {
      this.getAccessTokenSync();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get token expiration time
   */
  getTokenExpiration(): Date | null {
    if (this.tokenExpirationTime) {
      return new Date(this.tokenExpirationTime);
    }
    const tokenInfo = this.getStoredTokens();
    return tokenInfo ? new Date(tokenInfo.expiresAt) : null;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    if (!this.tokenExpirationTime) return true;
    return Date.now() >= this.tokenExpirationTime;
  }

  /**
   * Check if token is expiring soon (within threshold)
   */
  isTokenExpiring(): boolean {
    if (!this.tokenExpirationTime) return true;
    const timeUntilExpiry = this.tokenExpirationTime - Date.now();
    return timeUntilExpiry <= OAuthService.TOKEN_EXPIRY_THRESHOLD_MS;
  }

  /**
   * Get time until token expiration in milliseconds
   */
  getTimeUntilExpiration(): number | null {
    if (!this.tokenExpirationTime) return null;
    return Math.max(0, this.tokenExpirationTime - Date.now());
  }

  /**
   * Check if a token refresh is currently in progress
   */
  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }
}

// Export singleton instance
export const oAuthService = new OAuthService();
